/** Dashboard layout — shared between Student and Admin. Sidebar nav + main content. */
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { BrandMark } from "../../components/ui/Brand";
import { ThemeToggle } from "../../components/ui/ThemeToggle";
import { UserMenu } from "../auth/UserMenu";
import { cn } from "../../lib/cn";

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function DashboardLayout({ nav = [], title, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside
        className={cn(
          "lg:flex flex-col border-r border-line bg-bg-elev",
          mobileOpen ? "fixed inset-0 z-[120] flex" : "hidden lg:flex"
        )}
        data-testid="dashboard-sidebar"
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-line">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark size={28} />
            <div className="flex flex-col leading-none">
              <b className="font-display text-[16px]">{title || "Nikhil Vatika Lab"}</b>
              <span className="font-mono text-[9px] tracking-[0.22em] text-text-faint uppercase mt-1">
                NVL · {title === "Admin" ? "Console" : "Dashboard"}
              </span>
            </div>
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden w-9 h-9 rounded-full border border-line-2 inline-flex items-center justify-center"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" aria-label="Section navigation">
          {nav.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="px-3 py-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-text-faint">
                {group.label}
              </div>
              {group.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.exact}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`sidebar-link-${slugify(it.label)}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13.5px] transition-colors",
                      isActive
                        ? "bg-surface-2 text-text font-medium"
                        : "text-text-dim hover:text-text hover:bg-surface"
                    )
                  }
                >
                  {it.Icon && <it.Icon size={15} className="text-mauve" />}
                  {it.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-line">
          <Link
            to="/"
            className="block px-3 py-2 text-[12.5px] text-text-dim hover:text-text text-center rounded-lg hover:bg-surface transition-colors"
          >
            ← Back to NVL
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 flex items-center gap-3 px-5 lg:px-8 py-3.5 border-b border-line bg-glass-2 backdrop-blur-xl">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="lg:hidden w-9 h-9 rounded-full border border-line-2 inline-flex items-center justify-center"
            data-testid="dashboard-burger"
          >
            <MenuIcon size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] font-mono uppercase tracking-[0.18em] text-text-faint">
              {location.pathname.replace("/", "")}
            </div>
          </div>
          <ThemeToggle />
          <UserMenu />
        </header>
        <main className="flex-1 px-5 lg:px-8 py-8" data-testid="dashboard-main">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
